import { ClientLoggingService } from '../shared/client-logging.service';
import { Injectable } from '@angular/core';

export interface ComplianceComponentState {
  expandedSectionIds: Set<string>;
  selectedRelevanceValues: string[];
  selectedComplianceValues: string[];
  scrollPosition: number;
  lastActivePageId?: string;
}

/** Caches per-page compliance UI state (expanded sections, filter selections, scroll position) so it persists across tab switches. */
@Injectable({
  providedIn: 'root',
})
export class ComplianceStateService {
  private stateCache = new Map<string, ComplianceComponentState>();

  constructor() {}

  /**
   * Generate a unique key for the state based on plan ID and page number
   */
  private getStateKey(planId: string, pageNumber: number): string {
    return `${planId}-${pageNumber}`;
  }

  /**
   * Save the current state of the compliance component
   */
  saveState(planId: string, pageNumber: number, state: ComplianceComponentState): void {
    const key = this.getStateKey(planId, pageNumber);
    ClientLoggingService.log('INFO', `[ComplianceStateService] Saving state for ${key}:`, state);
    this.stateCache.set(key, {
      ...state,
      expandedSectionIds: new Set(state.expandedSectionIds), // Clone the Set
      selectedRelevanceValues: [...state.selectedRelevanceValues], // Clone the array
      selectedComplianceValues: [...state.selectedComplianceValues], // Clone the array
      lastActivePageId: key,
    });
  }

  /**
   * Restore the state of the compliance component
   */
  restoreState(planId: string, pageNumber: number): ComplianceComponentState | null {
    const key = this.getStateKey(planId, pageNumber);
    const state = this.stateCache.get(key);
    ClientLoggingService.log('INFO', `[ComplianceStateService] Restoring state for ${key}:`, state);
    return state || null;
  }

  /**
   * Clear state for a specific page
   */
  clearPageState(planId: string, pageNumber: number): void {
    const key = this.getStateKey(planId, pageNumber);
    ClientLoggingService.log('INFO', `[ComplianceStateService] Clearing state for ${key}`);
    this.stateCache.delete(key);
  }

  /**
   * Clear all states for a plan (when switching plans)
   */
  clearPlanStates(planId: string): void {
    ClientLoggingService.log('INFO', `[ComplianceStateService] Clearing all states for plan ${planId}`);
    const keysToDelete = Array.from(this.stateCache.keys()).filter(key => key.startsWith(`${planId}-`));
    keysToDelete.forEach(key => this.stateCache.delete(key));
  }

  /**
   * Clear all cached states
   */
  clearAllStates(): void {
    ClientLoggingService.log('INFO', '[ComplianceStateService] Clearing all cached states');
    this.stateCache.clear();
  }
}
